import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const HOME = { admin: "/admin", therapist: "/painel", client: "/app" };

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [kind, setKind] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setKind(data.kind);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 12) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  const goHome = () => navigate(HOME[user?.role] || "/");

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="p-10 max-w-md w-full text-center">
        <Brand className="justify-center mb-8" />
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
            <h1 className="font-heading text-xl font-semibold mt-5">Confirmando seu pagamento...</h1>
            <p className="text-muted-foreground text-sm mt-2">Aguarde um instante, estamos verificando com o Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-accent/15 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-accent" /></div>
            <h1 className="font-heading text-2xl font-semibold mt-5" data-testid="payment-success-title">Pagamento confirmado!</h1>
            <p className="text-muted-foreground mt-2">
              {kind === "subscription"
                ? "Sua assinatura foi ativada. Agora aguarde a aprovação do administrador para liberar o acesso completo."
                : kind === "content_purchase"
                ? "Conteúdo desbloqueado! Ele já está disponível na sua vitrine de conteúdos."
                : kind === "package"
                ? "Pacote adquirido! Suas sessões já estão disponíveis em Minhas Consultas."
                : "Sua consulta foi confirmada. Você já pode vê-la em Minhas Consultas."}
            </p>
            <Button className="rounded-full mt-6" onClick={goHome} data-testid="payment-home-btn">Ir para o painel</Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-destructive/15 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-destructive" /></div>
            <h1 className="font-heading text-2xl font-semibold mt-5">Não foi possível confirmar</h1>
            <p className="text-muted-foreground mt-2">O pagamento não foi concluído ou está demorando. Tente novamente.</p>
            <Button variant="outline" className="rounded-full mt-6" onClick={goHome} data-testid="payment-home-btn">Voltar ao painel</Button>
          </>
        )}
      </Card>
    </div>
  );
}
